"use client";
import { ReactFlowProvider } from "@xyflow/react";
import {
  Account,
  BuiltInApp,
  RawCanvas,
  Session,
  Toggles,
} from "@zapier/canvas-state";
import dynamic from "next/dynamic";
import { useEmitUserInteractionEvents } from "@zapier/observability";

import { ExecuteAiPrompt } from "../components/ExecuteAiPrompt";
import { Canvas } from "../components/Canvas";
import { CanvasHydrator } from "../components/CanvasHydrator";
import { CanvasBackground } from "../components/CanvasBackground";
import { CanvasCopilotHydrator } from "../components/CanvasCopilotHydrator";
import {
  useRegisterCopilotSendButtonEvent,
  useRegisterCopilotEnterKeyEvent,
} from "../hooks/useRegisterEventEmitter";

// Import BuilderNav as a client-only component to solve hydration issues
const BuilderNav = dynamic(
  () => import("../components/BuilderNav").then((mod) => mod.BuilderNav),
  { ssr: false },
);

type Props = {
  canvas: RawCanvas;
  chatbotsApp: BuiltInApp;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  agentsApp: BuiltInApp;
  session?: Session;
  account?: Account;
  toggles: Toggles;
  prompt?: string;
  isAgentsEnabled: boolean;
};

export function AiPage({
  canvas,
  chatbotsApp,
  tablesApp,
  interfacesApp,
  agentsApp,
  toggles,
  account,
  session,
  prompt,
  isAgentsEnabled,
}: Props) {
  const { emitEventOnCopilotMessageSent } = useEmitUserInteractionEvents({
    canvasId: canvas.id,
    sessionId: session?.id,
    currentAccountId: account?.id,
  });

  // Register both click and keyboard events
  useRegisterCopilotSendButtonEvent(emitEventOnCopilotMessageSent);
  useRegisterCopilotEnterKeyEvent(emitEventOnCopilotMessageSent);

  return (
    <ReactFlowProvider>
      <CanvasHydrator
        canvas={canvas}
        chatbotsApp={chatbotsApp}
        tablesApp={tablesApp}
        interfacesApp={interfacesApp}
        agentsApp={agentsApp}
        toggles={toggles}
        account={account}
        session={session}
        isAiPage={true}
        isAgentsEnabled={isAgentsEnabled}
        prompt={prompt}
      >
        <CanvasCopilotHydrator>
          <ExecuteAiPrompt prompt={prompt || ""} />
          <BuilderNav>
            <Canvas>
              <CanvasBackground />
            </Canvas>
          </BuilderNav>
        </CanvasCopilotHydrator>
      </CanvasHydrator>
    </ReactFlowProvider>
  );
}
